import { gsap } from 'gsap';
import { nextTick, computed } from 'vue';
import { useFeaturesStore } from '@/store/pinia/features';
import { storeToRefs } from 'pinia';

export function useUiAnimations() {
  const featuresStore = useFeaturesStore();
  const { noAnimations } = storeToRefs(featuresStore);

  // TODO: put back into the constants once refactor is done
  const BASE_SPEED = 0.6;
  const BASE_EASE = 'power2';
  const BASE_EASE_IN = BASE_EASE + '.in';
  const BASE_EASE_OUT = BASE_EASE + '.out';
  const BASE_EASE_IN_OUT = BASE_EASE + '.inOut';

  const doAnimations = computed(() => !noAnimations.value);

  function animationGuard(fn, alternativeFn) {
    const promise = new Promise((resolve) => {
      if (doAnimations.value) {
        if (fn && typeof fn.then === 'function') {
          fn().then(resolve);
        } else {
          fn();
          resolve();
        }
      } else if (alternativeFn && typeof alternativeFn === 'function') {
        alternativeFn();
        resolve();
      } else {
        resolve();
      }
    });
    return promise;
  }

  function nopeWaggle($el, args = {}) {
    const totalDuration = args.duration || 0.3;
    return animationGuard(() => {
      var tl = gsap.timeline();
      tl.to($el, { rotation: 15, duration: totalDuration * 0.2 })
        .to($el, { rotation: -10, duration: totalDuration * 0.2 })
        .to($el, { rotation: 5, duration: totalDuration * 0.2 })
        .to($el, { rotation: -5, duration: totalDuration * 0.2 })
        .to($el, { rotation: 0, duration: totalDuration * 0.2 });
      return tl;
    });
  }

  function pulse($el, args = {}) {
    const totalDuration = args.duration || 0.3;
    return animationGuard(() => {
      var tl = gsap.timeline();
      tl.to($el, { scale: 1.1, duration: totalDuration * 0.5, ease: 'expo.inOut' }).to($el, { scale: 1, duration: totalDuration * 0.5, ease: 'expo.inOut' });
      return tl;
    });
  }

  const animateIn = {
    blip($el, args = {}) {
      return animationGuard(
        () => {
          return gsap.fromTo(
            $el,
            { opacity: 0, scale: 0 },
            { duration: BASE_SPEED, opacity: 1, scale: 1, display: 'block', ease: 'elastic.out(1, 0.5)', ...args },
          );
        },
        () => {
          return gsap.set($el, { opacity: 1, scale: 1, display: 'block' });
        },
      );
    },
    grow($el, args = {}) {
      return animationGuard(
        () => {
          return gsap.fromTo($el, { scale: 0 }, { duration: BASE_SPEED, scale: 1, ease: BASE_EASE_OUT, ...args });
        },
        () => {
          return gsap.set($el, { scale: 1, display: 'block', opacity: 1 });
        },
      );
    },
    flip($el, args = {}) {
      return animationGuard(
        () => {
          return gsap.fromTo($el, { rotationX: 90 }, { duration: BASE_SPEED, rotationX: 0, ease: BASE_EASE_IN, ...args });
        },
        () => {
          return gsap.set($el, { rotationX: 0 });
        },
      );
    },
    fade($el, args = {}) {
      return animationGuard(
        () => {
          return gsap.fromTo($el, { opacity: 0 }, { duration: BASE_SPEED, opacity: 1, ease: BASE_EASE_OUT, ...args });
        },
        () => {
          return gsap.set($el, { opacity: 1 });
        },
      );
    },
  };

  const animateOut = {
    blip($el, args = {}) {
      return animationGuard(
        () => {
          return gsap.to($el, { duration: BASE_SPEED / 2, opacity: 0, scale: 0, ease: BASE_EASE_OUT, ...args });
        },
        () => {
          return gsap.set($el, { opacity: 0, scale: 0, display: 'none' });
        },
      );
    },
    shrink($el, args = {}) {
      return animationGuard(
        () => {
          return gsap.to($el, { duration: BASE_SPEED, scale: 0, ease: BASE_EASE_IN, ...args });
        },
        () => {
          return gsap.set($el, { scale: 0 });
        },
      );
    },
    flip($el, args = {}) {
      return animationGuard(
        () => {
          return gsap.fromTo($el, { rotationX: 0 }, { duration: BASE_SPEED, rotationX: 90, ease: BASE_EASE_OUT, ...args });
        },
        () => {
          return gsap.set($el, { rotationX: '90deg' });
        },
      );
    },
    fade($el, args = {}) {
      return animationGuard(
        () => {
          return gsap.to($el, { duration: BASE_SPEED, opacity: 0, ease: BASE_EASE_IN, ...args });
        },
        () => {
          return gsap.set($el, { opacity: 0 });
        },
      );
    },
  };

  const hideShowSets = {
    fade: { enter: animateIn.fade, leave: animateOut.fade },
    scale: { enter: animateIn.grow, leave: animateOut.shrink },
    flip: { enter: animateIn.flip, leave: animateOut.flip },
    blip: { enter: animateIn.blip, leave: animateOut.blip },
    bubblePop: { enter: animateIn.blip, leave: animateOut.blip, split: 0.75 },
  };

  function hiddenUpdate($el, fn, animation = 'fade', args = {}) {
    const promise = new Promise((resolve) => {
      if (!doAnimations.value) {
        fn();
        resolve();
      } else {
        const duration = args.duration || BASE_SPEED;
        const pause = args.pause || 0;
        if (!$el) {
          return;
        }
        const { enter, leave, split } = hideShowSets[animation] || hideShowSets.fade;
        let leaveDuration, enterDuration;
        if (split) {
          enterDuration = duration * split;
          leaveDuration = duration - enterDuration;
        } else {
          leaveDuration = duration / 2;
          enterDuration = duration / 2;
        }
        leave($el, { duration: leaveDuration }).then(() => {
          fn();
          nextTick(() => {
            setTimeout(() => {
              if (!$el) {
                resolve();
              } else {
                enter($el, { duration: enterDuration }).then(resolve);
              }
            }, pause * 1000); // pause given is in seconds
          });
        });
      }
    });
    return promise;
  }

  return { animationGuard, nopeWaggle, pulse, animateIn, animateOut, hiddenUpdate, BASE_SPEED, BASE_EASE_IN, BASE_EASE_OUT, BASE_EASE_IN_OUT };
}
